import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import useSessionStorage from './useSessionStorage'
import { searchDataByAttribute } from '@helpers/contentful'
import { FlashBannerItem } from 'types/contentful/graphQlApi'
import trackError from '@helpers/trackError'

const SESSION_KEY = 'spoke_campaign'

type UseCampaignBannerResult = {
  campaignBanner: FlashBannerItem | null
  campaignSlug: string
  isLoading: boolean
}

export function useCampaignBanner(): UseCampaignBannerResult {
  const router = useRouter()
  const [storedSlug, setStoredSlug] = useSessionStorage<string>(SESSION_KEY, '')
  const [campaignBanner, setCampaignBanner] = useState<FlashBannerItem | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)

  const urlSlug = router.query.campaign as string | undefined
  const activeSlug = urlSlug || storedSlug

  // Persist the URL slug to session so the banner survives page navigation
  useEffect(() => {
    if (urlSlug && urlSlug !== storedSlug) {
      setStoredSlug(urlSlug)
    }
  }, [urlSlug, storedSlug, setStoredSlug])

  useEffect(() => {
    if (!activeSlug) {
      setCampaignBanner(null)
      setIsLoading(false)
      return
    }

    let isMounted = true
    setIsLoading(true)

    const fetchBanner = async () => {
      try {
        const result = await searchDataByAttribute<FlashBannerItem>({
          content_type: 'flashBannerItemWithOffer',
          'fields.bannerName': activeSlug
        })

        if (isMounted) {
          setCampaignBanner(
            Object.keys(result).length > 0 ? (result as FlashBannerItem) : null
          )
          setIsLoading(false)
        }
      } catch (error) {
        if (isMounted) {
          setCampaignBanner(null)
          setIsLoading(false)
        }
        trackError(
          new Error('Failed to fetch campaign banner from Contentful'),
          { section: 'useCampaignBanner', error: error as Error, activeSlug }
        )
      }
    }

    fetchBanner()

    return () => {
      isMounted = false
    }
  }, [activeSlug])

  return { campaignBanner, campaignSlug: activeSlug, isLoading }
}
